import { fetchAuthSession } from 'aws-amplify/auth'
import { config } from '../config'

export class ApiError extends Error {
  status: number
  code?: string
  constructor(message: string, status: number, code?: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

async function authHeader(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    return token ? { Authorization: `Bearer ${token}` } : {}
  } catch {
    return {}
  }
}

/** Authenticated JSON request. */
export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(await authHeader()),
    ...((options.headers as Record<string, string>) || {}),
  }
  const res = await fetch(`${config.apiEndpoint}${path}`, { ...options, headers })
  return parse<T>(res)
}

/** Public (no-auth) JSON request. */
export async function publicFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${config.apiEndpoint}${path}`, {
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
  })
  return parse<T>(res)
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text()
  const data = text ? JSON.parse(text) : {}
  if (!res.ok) {
    const err = data?.error
    throw new ApiError(err?.message || res.statusText, res.status, err?.code)
  }
  return data as T
}

// ── Media upload ────────────────────────────────────────────────────────────

/** Downscale + recompress an image client-side to keep within the storage cap. */
export async function optimizeImage(file: File, maxDim = 1400, quality = 0.82): Promise<Blob> {
  if (!file.type.startsWith('image/')) return file
  const bitmap = await createImageBitmap(file).catch(() => null)
  if (!bitmap) return file
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height))
  const w = Math.round(bitmap.width * scale)
  const h = Math.round(bitmap.height * scale)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(bitmap, 0, 0, w, h)
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b || file), 'image/jpeg', quality))
}

/** Upload a file to S3 via a presigned PUT and return the stored key. */
export async function signAndUpload(
  horseId: string,
  file: File,
  category: 'photos' | 'profile' | 'news' | 'documents' | 'medical',
): Promise<{ key: string; sizeBytes: number; name: string; contentType: string }> {
  const isImage = file.type.startsWith('image/')
  const blob = isImage ? await optimizeImage(file) : file
  const contentType = isImage ? 'image/jpeg' : file.type || 'application/octet-stream'
  const ext = isImage ? 'jpg' : (file.name.split('.').pop() || '').toLowerCase()

  const { url, key } = await apiFetch<{ url: string; key: string }>(`/api/horses/${horseId}/upload-url`, {
    method: 'POST',
    body: JSON.stringify({ category, contentType, ext, sizeBytes: blob.size }),
  })
  const put = await fetch(url, { method: 'PUT', headers: { 'Content-Type': contentType }, body: blob })
  if (!put.ok) throw new ApiError('Upload failed', put.status)
  return { key, sizeBytes: blob.size, name: file.name, contentType }
}

/** Upload an avatar via the /me/avatar-url presign; returns the key. */
export async function uploadAvatar(file: File): Promise<string> {
  const blob = await optimizeImage(file, 512)
  const { url, key } = await apiFetch<{ url: string; key: string }>(`/api/me/avatar-url`, {
    method: 'POST',
    body: JSON.stringify({ contentType: 'image/jpeg', ext: 'jpg', sizeBytes: blob.size }),
  })
  const put = await fetch(url, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: blob })
  if (!put.ok) throw new ApiError('Upload failed', put.status)
  return key
}

/** Upload a farm avatar via the /farms/:id/avatar-url presign; returns the key. */
export async function uploadFarmAvatar(farmId: string, file: File): Promise<string> {
  const blob = await optimizeImage(file, 512)
  const { url, key } = await apiFetch<{ url: string; key: string }>(`/api/farms/${farmId}/avatar-url`, {
    method: 'POST',
    body: JSON.stringify({ contentType: 'image/jpeg', ext: 'jpg', sizeBytes: blob.size }),
  })
  const put = await fetch(url, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: blob })
  if (!put.ok) throw new ApiError('Upload failed', put.status)
  return key
}
